import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * F5-T2 · Los cuatro estados compartidos (mockup «Agente 04», ventana 10).
 *
 * Estructura FIJA siempre: icono · qué pasó · qué se pierde · UN botón.
 * «El vacío usa salvia y el error usa rojo, nunca al revés.»
 *
 * - Vacío (salvia): no es un error — describe una situación normal y ofrece
 *   la acción que la cambia.
 * - Error (rojo): dice qué falló, QUÉ SE PIERDE mientras falla, y un solo
 *   botón. Nunca jerga de servidor.
 * - 404 (neutro, no rojo): no falló nada — nombra la causa más probable.
 * - Cargando: esqueletos del tamaño real del contenido (la página no salta),
 *   sin spinner centrado, sin texto de «cargando…» si tarda <1s.
 *
 * El chasis es estructural: cada pantalla trae su copy (i18n del caller).
 */

interface StateAction {
  label: string;
  onClick: () => void;
}

function StateShell({
  icon,
  title,
  body,
  action,
  tone,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action?: StateAction;
  tone: 'sage' | 'red' | 'neutral';
}): JSX.Element {
  const iconWrap =
    tone === 'sage' ? 'bg-nav-sageTint text-nav-sageText'
    : tone === 'red' ? 'bg-nav-redTint text-nav-red'
    : 'bg-nav-soft text-nav-slate';
  const titleColor =
    tone === 'sage' ? 'text-nav-sageText'
    : tone === 'red' ? 'text-nav-red'
    : 'text-nav-navy';
  const btn =
    tone === 'red'
      ? 'bg-nav-red text-white hover:opacity-90'
      : 'bg-nav-navy text-white hover:bg-nav-navyHover';
  return (
    <div className="flex flex-col items-center text-center px-6 py-10" data-chassis="state">
      <div className={cn('w-12 h-12 rounded-full flex items-center justify-center mb-3', iconWrap)}>
        {icon}
      </div>
      <p className={cn('font-head font-semibold text-[16px] mb-1', titleColor)}>{title}</p>
      <p className="font-body text-[13.5px] text-nav-slate max-w-[420px] leading-relaxed">{body}</p>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className={cn('mt-4 min-h-[44px] px-5 rounded-lg font-body font-semibold text-[13px]', btn)}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

/** Vacío — salvia. `body` describe la situación normal; `action` la cambia. */
export function EmptyState(props: { icon: ReactNode; title: string; body: string; action?: StateAction }): JSX.Element {
  return <StateShell {...props} tone="sage" />;
}

/** Error — rojo. `body` DEBE decir qué se pierde mientras falla. Un solo botón. */
export function ErrorState(props: { icon: ReactNode; title: string; body: string; action?: StateAction }): JSX.Element {
  return <StateShell {...props} tone="red" />;
}

/** 404 / no-está — neutro, no rojo: no falló nada. Nombra la causa probable. */
export function NotFoundState(props: { icon: ReactNode; title: string; body: string; action?: StateAction }): JSX.Element {
  return <StateShell {...props} tone="neutral" />;
}

/**
 * Esqueleto del tamaño real del contenido. El caller dibuja tantos como filas
 * reales espera, con las clases de tamaño del contenido verdadero.
 */
export function Skeleton({ className }: { className?: string }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-nav-soft', className)}
      data-chassis="skeleton"
    />
  );
}
